import { PostCode } from "./services/listingsSearchCriteriaApi";

export type PostcodeSelectionState = {
    selectedCodes: Set<string>;
};

export type PostcodeIndex = {
    all: PostCode[];
    byCode: Map<string, PostCode>;
    childrenByParent: Map<string, PostCode[]>;
    districts: PostCode[];
    areas: PostCode[];
};

export function buildPostcodeIndex(postcodes: PostCode[]): PostcodeIndex {
    const byCode = new Map<string, PostCode>();
    const childrenByParent = new Map<string, PostCode[]>();
    const districts: PostCode[] = [];
    const areas: PostCode[] = [];

    for (const pc of postcodes) {
        byCode.set(pc.code, pc);

        if (pc.level === "DISTRICT") {
            districts.push(pc);
        }

        if (pc.level === "AREA") {
            areas.push(pc);
        }

        if (pc.parent) {
            const children = childrenByParent.get(pc.parent) ?? [];
            children.push(pc);
            childrenByParent.set(pc.parent, children);
        }
    }

    return {
        all: postcodes,
        byCode,
        childrenByParent,
        districts,
        areas,
    };
}

export function isEffectivelySelected(
    code: string,
    index: PostcodeIndex,
    state: PostcodeSelectionState,
): boolean {
    let cursor: string | undefined = code;

    while (cursor) {
        if (state.selectedCodes.has(cursor)) return true;

        const parentCode: string | null | undefined = index.byCode.get(cursor)?.parent;
        cursor = parentCode ?? undefined;
    }

    return false;
}

/**
 * True when some descendants are directly selected.
 * Used for partial-fill styling.
 */
export function isPartiallySelected(
    code: string,
    index: PostcodeIndex,
    state: PostcodeSelectionState,
): boolean {
    const stack = [...(index.childrenByParent.get(code) ?? [])];

    while (stack.length > 0) {
        const pc = stack.pop();
        if (!pc) continue;

        if (state.selectedCodes.has(pc.code)) {
            return true;
        }

        stack.push(...(index.childrenByParent.get(pc.code) ?? []));
    }

    return false;
}

export function areaCodeOf(code: string, index: PostcodeIndex): string {
    let cursor = code;

    while (true) {
        const pc = index.byCode.get(cursor);

        if (!pc?.parent) {
            return cursor;
        }

        cursor = pc.parent;
    }
}

export function togglePostcode(
    code: string,
    index: PostcodeIndex,
    state: PostcodeSelectionState,
): PostcodeSelectionState {
    const pc = index.byCode.get(code);
    if (!pc) return state;

    const selected = new Set(state.selectedCodes);

    if (isEffectivelySelected(code, index, state)) {
        expandAndRemove(code, index, selected);
    } else {
        for (const descendantCode of getDescendantCodes(code, index)) {
            selected.delete(descendantCode);
        }

        selected.add(code);
    }

    return normalizeSelection(index, { selectedCodes: selected });
}

/**
 * Expand the selected ancestor chain down to `code`, then remove `code`.
 *
 * Example — 'SW' is selected, target is 'SW1H 0':
 *   chain = ['SW', 'SW1H', 'SW1H 0']
 *   expand SW  → remove SW,  add {SW1A, SW1E, SW1H, SW1P, …}
 *   expand SW1H → remove SW1H, add {SW1H 0, SW1H 1, …}
 *   remove SW1H 0
 */
function expandAndRemove(
    code: string,
    index: PostcodeIndex,
    selected: Set<string>,
): void {
    if (selected.has(code)) {
        selected.delete(code);
        return;
    }

    const chain: string[] = [code];
    let cursor = code;

    while (true) {
        const pc = index.byCode.get(cursor);
        if (!pc?.parent) break;

        chain.unshift(pc.parent);
        cursor = pc.parent;

        if (selected.has(cursor)) break;
    }

    for (let i = 0; i < chain.length - 1; i++) {
        const parent = chain[i];

        if (selected.has(parent)) {
            selected.delete(parent);

            for (const child of index.childrenByParent.get(parent) ?? []) {
                selected.add(child.code);
            }
        }

        if (i === chain.length - 2) {
            selected.delete(chain[chain.length - 1]);
        }
    }
}

/**
 * Collapse upward:
 * - all sectors selected => district selected
 * - all districts selected => area selected
 */
function normalizeSelection(
    index: PostcodeIndex,
    state: PostcodeSelectionState,
): PostcodeSelectionState {
    const selected = new Set(state.selectedCodes);

    // Sectors → Districts
    for (const district of index.districts) {
        if (selected.has(district.code)) continue;

        const sectors = index.childrenByParent.get(district.code) ?? [];

        if (sectors.length > 0 && sectors.every(sector => selected.has(sector.code))) {
            selected.add(district.code);

            for (const sector of sectors) {
                selected.delete(sector.code);
            }
        }
    }

    // Districts → Areas
    for (const area of index.areas) {
        if (selected.has(area.code)) continue;

        const districts = index.childrenByParent.get(area.code) ?? [];

        if (districts.length > 0 && districts.every(district => selected.has(district.code))) {
            selected.add(area.code);

            for (const district of districts) {
                selected.delete(district.code);

                for (const sector of index.childrenByParent.get(district.code) ?? []) {
                    selected.delete(sector.code);
                }
            }
        }
    }

    return { selectedCodes: selected };
}

function getDescendantCodes(code: string, index: PostcodeIndex): string[] {
    const result: string[] = [];
    const stack = [...(index.childrenByParent.get(code) ?? [])];

    while (stack.length > 0) {
        const pc = stack.pop();
        if (!pc) continue;

        result.push(pc.code);
        stack.push(...(index.childrenByParent.get(pc.code) ?? []));
    }

    return result;
}
